import { useContext, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Popover from '@mui/material/Popover';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
	ANGLE_LINES,
	BOOK_INFO_DEFAULTS,
	IMAGETYPE,
} from '../../../config/constants';
import { PatternContext } from '../../../providers/PatternProvider';
import {
	modalBackdropSx,
	modalContentStyle,
	flexSpacerStyle,
} from '../../../styles/patterns';
import { COLOR_MENU_TEXT } from '../../../styles/tokens';

const menuTextSx = { fontSize: '1.5rem', color: COLOR_MENU_TEXT };

export const AdvancedSettingsModal = (props) => {
	const { showAdvancedModal, setShowAdvancedModal, imageType, setImageType } =
		props;

	const {
		angleLine: angleLineObj,
		isInkSaver: isInkSaverObj,
		extraFrontPages: extraFrontPagesObj,
		extraBackPages: extraBackPagesObj,
	} = useContext(PatternContext);

	const { value: angleLine, setValue: setAngleLine } = angleLineObj;
	const { value: isInkSaver, setValue: setIsInkSaver } = isInkSaverObj;
	const { value: extraFrontPages, setValue: setExtraFrontPages } =
		extraFrontPagesObj;
	const { value: extraBackPages, setValue: setExtraBackPages } =
		extraBackPagesObj;

	// Ink saver
	const [anchorInfoInkSaver, setAnchorInfoInkSaver] = useState(null);
	const openPopoverInkSaver = Boolean(anchorInfoInkSaver);
	const inkSaverPopupId = openPopoverInkSaver
		? 'ink-saver-popover'
		: undefined;
	const handleInfoInkSaverClick = (event) => {
		setAnchorInfoInkSaver(event.currentTarget);
	};
	const handleInfoInkSaverClose = () => {
		setAnchorInfoInkSaver(null);
	};
	const handleInkSaverChange = (event) => {
		setIsInkSaver(event.target.checked);
	};

	//Angle line
	const [anchorInfoAngleLine, setAnchorInfoAngleLine] = useState(null);
	const openPopoverAngleLine = Boolean(anchorInfoAngleLine);
	const angleLinePopupId = openPopoverAngleLine
		? 'angle-line-popover'
		: undefined;
	const handleInfoAngleLineClick = (event) => {
		setAnchorInfoAngleLine(event.currentTarget);
	};
	const handleInfoAngleLineClose = () => {
		setAnchorInfoAngleLine(null);
	};
	const handleAngleLineChange = (event) => {
		setAngleLine(event.target.value);
	};

	const handleImageTypeChange = (event) => {
		setImageType(event.target.value);
	};

	// Extra pages
	const [anchorInfoExtraPages, setAnchorInfoExtraPages] = useState(null);
	const openPopoverExtraPages = Boolean(anchorInfoExtraPages);
	const extraPagesPopupId = openPopoverExtraPages
		? 'extra-pages-popover'
		: undefined;
	const handleInfoExtraPagesClick = (event) => {
		setAnchorInfoExtraPages(event.currentTarget);
	};
	const handleInfoExtraPagesClose = () => {
		setAnchorInfoExtraPages(null);
	};

	const ExtraBackPages = extraPageConfig(extraBackPages, setExtraBackPages);
	const ExtraFrontPages = extraPageConfig(
		extraFrontPages,
		setExtraFrontPages,
	);

	return (
		<>
			<Modal
				open={showAdvancedModal}
				onClose={() => setShowAdvancedModal(false)}
				sx={modalBackdropSx}
			>
				<div style={modalContentStyle}>
					<IconButton
						edge="end"
						aria-label="Menu"
						onClick={() => {
							setShowAdvancedModal(false);
						}}
						style={{
							alignSelf: 'flex-end',
							paddingTop: '0px',
							paddingBottom: '0px',
						}}
					>
						<CloseIcon />
					</IconButton>

					<Typography
						variant="h3"
						style={{ lineHeight: '3rem', paddingBottom: '15px' }}
					>
						Advanced Settings
					</Typography>

					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: '15px',
						}}
					>
						{/* Pattern Type (Single/Lenticular) */}
						<FormControl>
							<FormLabel>
								<Typography variant="h5">
									Pattern Type
								</Typography>
							</FormLabel>
							<RadioGroup
								value={imageType}
								onChange={handleImageTypeChange}
							>
								<FormControlLabel
									value={IMAGETYPE.SINGLE}
									control={<Radio />}
									label="Single Image"
									componentsProps={{
										typography: { variant: 'h5' },
									}}
								/>
								<FormControlLabel
									value={IMAGETYPE.DOUBLE}
									control={<Radio />}
									label="Lenticular"
									componentsProps={{
										typography: { variant: 'h5' },
									}}
								/>
							</RadioGroup>
						</FormControl>

						<div style={flexSpacerStyle} />

						{/* Ink Saver */}
						<FormControl>
							<div
								style={{
									display: 'flex',
									alignItems: 'baseline',
								}}
							>
								<FormControlLabel
									control={
										<Switch
											checked={isInkSaver}
											onChange={handleInkSaverChange}
										/>
									}
									label="Ink Saver"
									componentsProps={{
										typography: { variant: 'h5' },
									}}
								/>
								<InfoOutlinedIcon
									onClick={handleInfoInkSaverClick}
									fontSize="small"
								/>
							</div>

							<Popover
								id={inkSaverPopupId}
								open={openPopoverInkSaver}
								anchorEl={anchorInfoInkSaver}
								onClose={handleInfoInkSaverClose}
								anchorOrigin={{
									vertical: 'top',
									horizontal: 'right',
								}}
							>
								<Typography
									variant="h6"
									style={{
										marginLeft: '10px',
										marginRight: '10px',
									}}
								>
									Use thinner strips to save on ink
								</Typography>
							</Popover>
						</FormControl>

						<div style={flexSpacerStyle} />

						{/* Angle Cut Line */}
						<FormControl>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									marginLeft: '20px',
									marginRight: '20px',
								}}
							>
								<FormControlLabel
									control={
										<>
											<Select
												variant="standard"
												value={angleLine}
												label="Angle Line"
												onChange={handleAngleLineChange}
												sx={menuTextSx}
											>
												<MenuItem
													value={ANGLE_LINES.NONE}
													sx={menuTextSx}
												>
													{ANGLE_LINES.NONE}
												</MenuItem>
												<MenuItem
													value={ANGLE_LINES.TOPONLY}
													sx={menuTextSx}
												>
													{ANGLE_LINES.TOPONLY}
												</MenuItem>
												<MenuItem
													value={
														ANGLE_LINES.BOTTOMONLY
													}
													sx={menuTextSx}
												>
													{ANGLE_LINES.BOTTOMONLY}
												</MenuItem>
												<MenuItem
													value={
														ANGLE_LINES.TOPANDBOTTOM
													}
													sx={menuTextSx}
												>
													{ANGLE_LINES.TOPANDBOTTOM}
												</MenuItem>
											</Select>
										</>
									}
									label={
										<Typography
											variant="h5"
											style={{ marginLeft: '10px' }}
										>
											45° cut lines
										</Typography>
									}
								/>
								<InfoOutlinedIcon
									onClick={handleInfoAngleLineClick}
									fontSize="small"
								/>
							</div>

							<Popover
								id={angleLinePopupId}
								open={openPopoverAngleLine}
								anchorEl={anchorInfoAngleLine}
								onClose={handleInfoAngleLineClose}
								anchorOrigin={{
									vertical: 'top',
									horizontal: 'right',
								}}
							>
								<Typography
									variant="h6"
									style={{
										marginLeft: '10px',
										marginRight: '10px',
									}}
								>
									Show lines to guide angle cuts at the top
									and bottom of each strip
								</Typography>
							</Popover>
						</FormControl>

						<div style={flexSpacerStyle} />

						{/* Extra Front/Back Pages */}
						<FormControl
							style={{
								display: 'flex',
								alignItems: 'center',
								flexDirection: 'column',
							}}
						>
							<FormLabel
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '10px',
								}}
							>
								<Typography variant="h5">
									Extra Pages
								</Typography>
								<InfoOutlinedIcon
									onClick={handleInfoExtraPagesClick}
									fontSize="small"
								/>
								<Popover
									id={extraPagesPopupId}
									open={openPopoverExtraPages}
									anchorEl={anchorInfoExtraPages}
									onClose={handleInfoExtraPagesClose}
									anchorOrigin={{
										vertical: 'top',
										horizontal: 'right',
									}}
								>
									<Typography
										variant="h6"
										style={{
											marginLeft: '10px',
											marginRight: '10px',
										}}
									>
										Extra pages at the start or end of your
										book
									</Typography>
								</Popover>
							</FormLabel>
							<div
								style={{
									display: 'flex',
									alignItems: 'baseline',
									gap: '10px',
								}}
							>
								<Typography
									variant="h5"
									style={{ width: '40px' }}
								>
									Start
								</Typography>
								<TextField
									style={{ width: '75px' }}
									variant="standard"
									inputProps={{
										inputMode: 'numeric',
										pattern: '[0-9]*',
										style: {
											fontSize: 24,
											marginBottom: '-10px',
										},
									}}
									InputProps={{
										endAdornment: (
											<Typography
												style={{
													marginBottom: '-10px',
												}}
											>
												pages
											</Typography>
										),
									}}
									color="primary"
									type="number"
									step="1"
									value={ExtraFrontPages.value}
									onChange={ExtraFrontPages.onChange}
									error={!ExtraFrontPages.isValid()}
									helperText={
										!ExtraFrontPages.isValid() && (
											<Typography>
												Invalid value
											</Typography>
										)
									}
								/>
							</div>
							<div
								style={{
									display: 'flex',
									alignItems: 'baseline',
									gap: '10px',
								}}
							>
								<Typography
									variant="h5"
									style={{ width: '40px' }}
								>
									End
								</Typography>
								<TextField
									style={{ width: '75px' }}
									variant="standard"
									inputProps={{
										inputMode: 'numeric',
										pattern: '[0-9]*',
										style: {
											fontSize: 24,
											marginBottom: '-10px',
										},
									}}
									InputProps={{
										endAdornment: (
											<Typography
												style={{
													marginBottom: '-10px',
												}}
											>
												pages
											</Typography>
										),
									}}
									color="primary"
									type="number"
									step="1"
									value={ExtraBackPages.value}
									onChange={ExtraBackPages.onChange}
									error={!ExtraBackPages.isValid()}
									helperText={
										!ExtraBackPages.isValid() && (
											<Typography>
												Invalid value
											</Typography>
										)
									}
								/>
							</div>
						</FormControl>
					</div>
				</div>
			</Modal>
		</>
	);
};

const extraPageConfig = (state, setState) => {
	const ExtraPagesConfig = {
		value: state,
		onChange: (e) => {
			let newValue = Number(e.target.value);
			if (newValue > BOOK_INFO_DEFAULTS.MAX_EXTRA_PAGES) {
				newValue = BOOK_INFO_DEFAULTS.MAX_EXTRA_PAGES;
			}
			if (newValue < 0) {
				newValue = 0;
			}
			setState(newValue.toString());
		},
		isValid: () => {
			return (
				state !== '' && //non-empty
				state >= 0 && //greater than or equal to 0
				state <= BOOK_INFO_DEFAULTS.MAX_EXTRA_PAGES && //less than 5000
				state % 1 === 0 && //no decimals
				state.toString().length <= 5 //prevent many 0s
			);
		},
	};
	return ExtraPagesConfig;
};
